/**
 * Custom Buy Box — `CustomBuyBox` Alpine component
 * ------------------------------------------------------------------
 * A theme-native rebuild of the Recharge subscription buy box that also
 * surfaces the "Trial Box" as a first-class option. Deployed on the Bamboo
 * and Recycled toilet-paper PDPs only (see product-template.liquid gate).
 *
 * Cart handoff: a direct POST to /cart/add.js. Recharge's
 * storefront-experiences.js monkey-patches window.fetch and auto-opens its
 * own shadow-DOM cart drawer when the add carries a selling_plan (the
 * response includes a selling_plan_allocation). We additionally fire the
 * theme's own `shapes:cart:afteradditem` event so the header cart count
 * stays in sync. The store's cart_type is "page" (no theme drawer), so there
 * is no double-drawer risk.
 *
 * Loaded as a classic (non-module) deferred script. It registers the Alpine
 * component using the theme's deferrable pattern (see data-island.bundle.js):
 * register on `alpine:init` if Alpine has not started yet, else immediately.
 * The buy box markup is a nested x-data inside the Product island, so Alpine
 * hydrates it when it inits the product tree.
 */
(function () {
  'use strict';

  // CSS reveal hooks, flipped here (not via Alpine) so they work regardless of
  // init order — mirroring how Varify reveals cohorts in the live test.
  //   .np-buybox-on  → show the custom mirror, hide the Recharge buy box
  //   .np-trial-on   → reveal the Trial toggle (still gated by eligibility)
  // In production Varify adds these classes. For dev/QA we also honour URL
  // params: ?buybox=mirror (force the mirror) and ?plan=free-trial (mirror +
  // trial, the organic deep-link).
  try {
    var qs = new URLSearchParams(window.location.search);
    var isTrialLink = qs.get('plan') === 'free-trial';
    if (isTrialLink || qs.get('buybox') === 'mirror') {
      document.documentElement.classList.add('np-buybox-on');
    }
    if (isTrialLink) {
      document.documentElement.classList.add('np-trial-on');
    }
  } catch (e) {
    /* no-op */
  }

  function defineCustomBuyBox() {
    window.Alpine.data('CustomBuyBox', function (config) {
      return {
        cfg: config,

        // ---- state ----
        mode: '24', // 'trial' | '24' | '48' | '96'
        purchaseType: 'subscribe', // 'subscribe' | 'onetime'
        sellingPlanId: null,
        loading: false,
        cartAddError: '',
        addedToCart: false,
        trialOn: false, // mirrors the `.np-trial-on` cohort hook (for grid sizing)
        modal: '', // open overlay: '' | 'trial' | 'subscribe'

        init: function () {
          // Default: 24-roll, Subscribe, first (lowest cadence = every 1.5 months).
          this.mode = '24';
          this.purchaseType = 'subscribe';
          this.sellingPlanId = this.firstPlanId('24');
          this.trialOn = document.documentElement.classList.contains('np-trial-on');

          var params = new URLSearchParams(window.location.search);

          // Honour ?variant=<id> (e.g. Google Shopping ads deep-link to a pack
          // size) — pre-select that pack. A matched pack WINS the initial
          // selection over the Trial tab below: the trial stays revealed and
          // selectable, just not pre-selected.
          var variantParam = params.get('variant');
          var variantMatched = false;
          if (variantParam) {
            var self = this;
            Object.keys(this.cfg.modes).forEach(function (key) {
              if (String(self.cfg.modes[key].variantId) === String(variantParam)) {
                self.selectMode(key);
                variantMatched = true;
              }
            });
          }

          // Deep-link: pre-open the Trial tab when eligible — but NOT when a
          // specific pack was deep-linked (then the pack is pre-selected and the
          // trial is left exposed/selectable but unselected).
          if (params.get('plan') === 'free-trial' && this.showTrial && !variantMatched) {
            this.selectMode('trial');
          }
        },

        // ---- derived ----
        get showTrial() {
          // Eligibility (UX gate) only — cohort visibility is layered via the
          // `.np-trial-on` CSS hook (Varify / deep-link). Real enforcement is
          // the Shopify validation function + orders/create webhook.
          return this.cfg.ordersCount === 0;
        },
        get isTrial() {
          return this.mode === 'trial';
        },
        get isSubscribe() {
          return !this.isTrial && this.purchaseType === 'subscribe';
        },
        get currentMode() {
          // Fall back to a real size when the Trial tab is active ('trial' has no
          // entry in cfg.modes). The Subscribe/one-time block is hidden then, but
          // Alpine still evaluates its `currentMode.*` bindings — returning null
          // here throws a (harmless, console-only) error. The fallback is never shown.
          return this.cfg.modes[this.mode] || this.cfg.modes['24'];
        },
        get currentVariantId() {
          return this.isTrial ? this.cfg.trial.variantId : this.currentMode.variantId;
        },
        get effectiveSellingPlanId() {
          if (this.isTrial) return this.cfg.trial.sellingPlanId;
          if (this.purchaseType === 'subscribe') return this.sellingPlanId;
          return null;
        },
        get displayPriceCents() {
          if (this.isTrial) return 0;
          return this.isSubscribe ? this.currentMode.subCents : this.currentMode.oneTimeCents;
        },
        get comparePriceCents() {
          // Subscribe shows the one-time price struck through.
          if (!this.isSubscribe) return null;
          return this.currentMode.oneTimeCents;
        },
        get currentPlans() {
          return this.currentMode ? this.currentMode.plans : [];
        },
        get sizeKeys() {
          return ['24', '48', '96'];
        },
        get addToCartLabel() {
          return 'Add to Cart';
        },

        // ---- helpers ----
        sizeLabel: function (key) {
          return key + ' Rolls';
        },
        firstPlanId: function (mode) {
          var m = this.cfg.modes[mode];
          return m && m.plans.length ? m.plans[0].id : null;
        },
        money: function (cents) {
          if (typeof window.formatMoney === 'function') return window.formatMoney(cents);
          return '£' + (Number(cents) / 100).toFixed(2);
        },

        // ---- actions ----
        selectMode: function (mode) {
          this.mode = mode;
          this.cartAddError = '';
          this.addedToCart = false;
          if (mode !== 'trial' && this.purchaseType === 'subscribe') {
            // Reset to that size's first/lowest plan (every 1.5 months for 24).
            this.sellingPlanId = this.firstPlanId(mode);
          }
        },
        setPurchaseType: function (type) {
          this.purchaseType = type;
          this.cartAddError = '';
          this.addedToCart = false;
          if (type === 'subscribe' && !this.isTrial) {
            this.sellingPlanId = this.sellingPlanId || this.firstPlanId(this.mode);
          }
        },
        selectPlan: function (id) {
          this.sellingPlanId = id;
        },

        addToCart: function () {
          if (this.loading) return;
          this.loading = true;
          this.cartAddError = '';
          this.addedToCart = false;

          var item = { id: this.currentVariantId, quantity: 1 };
          var plan = this.effectiveSellingPlanId;
          if (plan) item.selling_plan = plan;
          if (this.isTrial) item.properties = { _trial_item: 'true' };

          var url =
            (window.theme && window.theme.routes && window.theme.routes.cart_add_url) ||
            '/cart/add.js';

          var self = this;
          fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [item] })
          })
            .then(function (res) {
              if (!res.ok) {
                return res.json().then(function (e) {
                  throw e;
                });
              }
              return res.json();
            })
            .then(function () {
              self.addedToCart = true;
              // We deliberately do NOT dispatch the theme's shapes:cart:afteradditem
              // event. On this Recharge store the cart is owned by Recharge: its
              // fetch patch (storefront-experiences.js) opens its own drawer for the
              // add, and the native (Recharge) adds don't fire the theme cart event
              // either — so the theme header count is page-load only store-wide.
              // Mirroring that keeps the buy box behaving like the Recharge control.
            })
            .catch(function (err) {
              self.cartAddError =
                (err && err.description) ||
                'Sorry, something went wrong adding to your basket. Please try again.';
            })
            .finally(function () {
              self.loading = false;
            });
        }
      };
    });
  }

  if (window.alpineIsInitialized && window.Alpine) {
    defineCustomBuyBox();
  } else {
    document.addEventListener('alpine:init', defineCustomBuyBox);
  }
})();
